import { useId } from 'react';
import {
  GROUP_BY_OPTIONS,
  type GroupByKey,
  groupByLabel,
} from '../lib/eventGrouping';

export interface GroupByOption<K extends string> {
  key: K;
  label: string;
}

interface GroupBySelectorProps<K extends string> {
  /** Ordered grouping levels (outer → inner). Empty = no grouping. */
  value: K[];
  /** Receives the new ordered list. */
  onChange: (next: K[]) => void;
  /** Available keys + display labels. Defaults to the event grouping options. */
  options?: GroupByOption<K>[];
  /** Optional label resolver (used in the "level1 → level2" summary line). */
  labelFor?: (key: K) => string;
  /** Hard cap on nesting depth. Defaults to 4 — deeper trees become unreadable. */
  maxDepth?: number;
  /** Optional override for the labelled control id. */
  idPrefix?: string;
  className?: string;
}

/**
 * Multi-level group-by picker. Renders one `<select>` per active level plus a
 * trailing "Add grouping" select while the depth limit hasn't been reached.
 * Each level only offers fields not already used at an outer level. Users can
 * remove any level (later levels move up to fill the gap) or clear all groups.
 *
 * Generic over the key type so the same component can drive event grouping
 * (default) and venue grouping. Defaults preserve the previous event-only
 * behaviour for existing callers.
 */
export default function GroupBySelector<K extends string = GroupByKey>({
  value,
  onChange,
  options,
  labelFor,
  maxDepth = 4,
  idPrefix,
  className,
}: GroupBySelectorProps<K>) {
  const generatedId = useId();
  const baseId = idPrefix ?? `mh-groupby-${generatedId}`;

  const opts = options ?? (GROUP_BY_OPTIONS as unknown as GroupByOption<K>[]);
  const resolveLabel = (k: K): string => {
    if (labelFor) return labelFor(k);
    const found = opts.find((o) => o.key === k);
    if (found) return found.label;
    return groupByLabel(k as unknown as GroupByKey);
  };

  const setLevel = (index: number, key: K | '') => {
    const next = [...value];
    if (key === '') {
      next.splice(index, 1);
    } else {
      next[index] = key;
    }
    onChange(dedupe(next));
  };

  const addLevel = (key: K) => {
    onChange(dedupe([...value, key]));
  };

  const clearAll = () => onChange([]);

  const remaining = (active: K[], allowIndex?: number): GroupByOption<K>[] => {
    const blocked = new Set(active.filter((_, i) => i !== allowIndex));
    return opts.filter((o) => !blocked.has(o.key));
  };

  const canAddMore = value.length < maxDepth && remaining(value).length > 0;

  return (
    <div className={`mh-groupby ${className ?? ''}`.trim()}>
      <span id={`${baseId}-label`} className="form-label small fw-semibold mb-1 d-block">
        Group by
      </span>
      <div className="d-flex flex-wrap gap-2 align-items-center" role="group" aria-labelledby={`${baseId}-label`}>
        {value.map((key, index) => {
          const selectId = `${baseId}-level-${index}`;
          const levelOptions = remaining(value, index);
          return (
            <div key={`${index}-${key}`} className="d-flex align-items-center gap-1">
              {index > 0 && (
                <span className="text-muted small" aria-hidden="true">
                  →
                </span>
              )}
              <label htmlFor={selectId} className="visually-hidden">
                Group level {index + 1}
              </label>
              <select
                id={selectId}
                className="form-select form-select-sm w-auto"
                value={key}
                onChange={(e) => setLevel(index, e.target.value as K | '')}
              >
                {levelOptions.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
                <option value="">— Remove —</option>
              </select>
            </div>
          );
        })}
        {canAddMore && (
          <div className="d-flex align-items-center gap-1">
            {value.length > 0 && (
              <span className="text-muted small" aria-hidden="true">
                →
              </span>
            )}
            <label htmlFor={`${baseId}-add`} className="visually-hidden">
              Add another grouping level
            </label>
            <select
              id={`${baseId}-add`}
              className="form-select form-select-sm w-auto"
              value=""
              onChange={(e) => {
                const v = e.target.value as K | '';
                if (v) addLevel(v);
              }}
            >
              <option value="">
                {value.length === 0 ? 'No grouping' : 'Add grouping…'}
              </option>
              {remaining(value).map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {value.length > 0 && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={clearAll}
          >
            Clear
          </button>
        )}
      </div>
      {value.length > 1 && (
        <p className="text-muted small mb-0 mt-1">
          {value.map(resolveLabel).join(' → ')}
        </p>
      )}
    </div>
  );
}

function dedupe<K extends string>(keys: K[]): K[] {
  const seen = new Set<K>();
  const out: K[] = [];
  for (const k of keys) {
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}
